import './OrderWizard.scss';

import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router';
import { useElements, useStripe } from '@stripe/react-stripe-js';

import { FormPanel } from '@components/FormPanel/FormPanel';
import { TurnstileAnchor } from '@components/Turnstile/TurnstileSlot';
import { BRIDGE_BIO_CAMPAIGN_CODE } from '@constants/bridgeBio';

import { BrowserBackWarning } from './components/BrowserBackWarning';
import { OrderSummaryAside } from './components/OrderSummaryAside';
import QuestionnaireWarningOffcanvas from './components/QuestionnaireWarningOffcanvas';
import Stepper from './components/Stepper';
import { WizardActions } from './components/WizardActions';
import { WizardStepRenderer } from './components/WizardStepRenderer';
import { useOrderContext } from './hooks/useOrderContext';
import { useWizardConsents } from './hooks/useWizardConsents';
import { useWizardContinue } from './hooks/useWizardContinue';
import { useWizardForm } from './hooks/useWizardForm';
import { useWizardPayment } from './hooks/useWizardPayment';
import { useWizardSteps } from './hooks/useWizardSteps';
import { useWizardSubmission } from './hooks/useWizardSubmission';
import { useWizardValidation } from './hooks/useWizardValidation';

// partnerConfig loads async for API-config partners (e.g. BridgeBio). WizardContent's hooks
// capture minor gating at mount (form defaults, consent fetch), so wait for the config to
// resolve before mounting them — otherwise a refresh mid-wizard drops the minor consent.
export function OrderWizard() {
  const { isLoadingPartner } = useOrderContext();

  if (isLoadingPartner) {
    return (
      <div className='wizard-page d-flex align-items-center justify-content-center'>
        <Spinner animation='border' role='status' />
      </div>
    );
  }

  return <WizardContent />;
}

function WizardContent() {
  const {
    campaign_code: campaignCode,
    state: {
      subject,
      guardian,
      family_history,
      paymentIntent,
      pricing,
      products,
      selected_product_ids: selectedProductIds,
      is_rerequisition: isRerequisition,
    },
    dispatch,
    partner,
    isMinorOrder,
    requiresPaymentStep,
    requiresQuestionnaireStep,
    selectedProducts,
    deselectProduct,
    selectProduct,
  } = useOrderContext();

  const [searchParams] = useSearchParams();

  // UI-specific state (declared early so hooks can use setShowErrors)
  const [showErrors, setShowErrors] = useState(false);
  const [showMessage, setShowMessage] = useState(false);

  // Stripe hooks - must be called at component level, not in custom hooks
  const stripe = useStripe();
  const elements = useElements();

  // Get promo code from URL to pre-fill promo code, but prioritize campaign_code
  const promoCodeFromUrl = searchParams.get('promo_code');

  // Default to value from URL, then override validated value from state
  let promoCode = promoCodeFromUrl || null;
  if (pricing?.promo_code) {
    promoCode = pricing.promo_code;
  }

  // Custom hooks
  const { steps, currentStep, goNext, goPrev, animDir, animCount } = useWizardSteps({
    isRerequisition,
    requiresPaymentStep,
    requiresQuestionnaireStep,
  });

  const isBridgeBioCampaign = campaignCode === BRIDGE_BIO_CAMPAIGN_CODE;
  const includeHIEConsent = !isBridgeBioCampaign;
  const includeResearchConsent = !isBridgeBioCampaign;
  const includeFamilyHistoryAttest = !isBridgeBioCampaign;

  const { values, updateValue, questionTexts, flushStorage } = useWizardForm({
    subject,
    family_history,
    dispatch,
    promoCode,
    isMinorOrder,
    includeHIEConsent,
  });

  const { handlePaymentConfirmation, stripeError, loadingPayment } = useWizardPayment({
    paymentIntent,
    values,
    updateValue,
    stripe,
    elements,
  });

  const {
    allConsents,
    dnaVisitConsent,
    hieConsent,
    researchConsent,
    transactionalSmsConsent,
    marketingConsent,
    minorConsent,
    loadingConsents,
    consentsFailed,
    fetchConsentsData,
  } = useWizardConsents({
    products: selectedProducts,
    steps,
    currentStep,
    includeMinorConsent: isMinorOrder,
    includeHIEConsent,
    includeResearchConsent,
    // Product consents are part of the informed-consent text, so a changed
    // selection invalidates an acceptance the patient already gave.
    onProductsChanged: () => updateValue('consents.acceptGeneral', false),
  });

  const { isCurrentStepComplete, currentStepErrors } = useWizardValidation({
    steps,
    currentStep,
    values,
    stripeError,
    isMinorOrder,
    includeFamilyHistoryAttest,
  });

  const { handleSubmitOrderClick, submitting } = useWizardSubmission({
    values,
    campaign_code: campaignCode,
    subject,
    guardian,
    family_history,
    products: selectedProducts,
    allConsents,
    dnaVisitConsent,
    hieConsent,
    researchConsent,
    transactionalSmsConsent,
    marketingConsent,
    minorConsent,
    dispatch,
    partner,
    isCurrentStepComplete,
    setShowErrors,
    flushStorage,
    isRerequisition,
  });

  // Handle continue click and canvas close logic
  const { handleContinueClick, handleCanvasClose } = useWizardContinue({
    steps,
    currentStep,
    isCurrentStepComplete,
    values,
    handlePaymentConfirmation,
    setShowErrors,
    setShowMessage,
    goNext,
  });

  useEffect(() => {
    // Reset error visibility when changing steps
    setShowErrors(false);
  }, [currentStep]);

  const getContinueButtonLabel = () => {
    if (steps[currentStep] === 'payment' && loadingPayment) {
      return 'Processing…';
    }

    return 'Continue';
  };

  const isContinueDisabled = selectedProducts.length === 0 || (steps[currentStep] === 'payment' && loadingPayment);

  const isSubmitDisabled =
    selectedProducts.length === 0 ||
    submitting ||
    loadingConsents ||
    consentsFailed ||
    (!dnaVisitConsent && allConsents.length === 0);

  return (
    <>
      <div className='wizard-page'>
        <FormPanel maxWidth='lg' layout='sectioned' className='wizard'>
          <FormPanel.Header title='Complete your details' subtitle='A few quick steps before we submit your order.' />
          <FormPanel.Body className='form-panel__body--flush'>
            <div className='wizard__layout'>
              <div className='wizard__main'>
                <div className='wizard__stepper'>
                  <Stepper total={steps.length} current={currentStep} startFrom={1} />
                </div>
                <form className='wizard__form' onSubmit={e => e.preventDefault()} noValidate>
                  <div
                    key={animCount}
                    className={classNames('wizard__step', {
                      'wizard__step--next': animDir === 'next',
                      'wizard__step--prev': animDir === 'prev',
                    })}
                  >
                    <WizardStepRenderer
                      currentStep={steps[currentStep]}
                      values={values}
                      showErrors={showErrors}
                      errors={currentStepErrors}
                      updateValue={updateValue}
                      stripeError={stripeError ?? null}
                      stripe={stripe}
                      elements={elements}
                      questionTexts={questionTexts}
                      allConsents={allConsents}
                      dnaVisitConsent={dnaVisitConsent}
                      hieConsent={hieConsent}
                      researchConsent={researchConsent}
                      transactionalSmsConsent={transactionalSmsConsent}
                      marketingConsent={marketingConsent}
                      minorConsent={minorConsent}
                      loadingConsents={loadingConsents}
                      consentsFailed={consentsFailed}
                      fetchConsentsData={fetchConsentsData}
                      pricing={pricing}
                      includeFamilyHistoryAttest={includeFamilyHistoryAttest}
                    />
                  </div>
                  <WizardActions
                    currentStep={currentStep}
                    totalSteps={steps.length}
                    onBack={goPrev}
                    onContinue={handleContinueClick}
                    onSubmit={handleSubmitOrderClick}
                    isContinueDisabled={isContinueDisabled}
                    isSubmitDisabled={isSubmitDisabled}
                    submitting={submitting}
                    continueLabel={getContinueButtonLabel()}
                  />
                  <div className='d-flex justify-content-end mt-3'>
                    <TurnstileAnchor />
                  </div>
                </form>
              </div>
              <OrderSummaryAside
                products={products || []}
                selectedProductIds={selectedProductIds}
                onDeselectProduct={deselectProduct}
                onSelectProduct={selectProduct}
              />
            </div>
          </FormPanel.Body>
        </FormPanel>
      </div>

      <QuestionnaireWarningOffcanvas
        show={showMessage}
        onHide={() => setShowMessage(false)}
        onProceed={handleCanvasClose}
      />
      <BrowserBackWarning />
    </>
  );
}
